import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useTranslation } from 'react-i18next';

// Altura máxima da imagem no modo "ajustado": janela menos cabeçalho, rodapé e margens do modal
const MAX_IMG_HEIGHT = 'calc(100vh - 220px)';
// Acima do painel expandido ($panel-expand-zindex = 1080)
const VIEWER_Z_INDEX = 1090;

/**
 * Visualizador de imagens da ficha.
 * - Modo ajustado (padrão): imagem em 100% ou menor, sem passar da janela.
 * - Clique na imagem: alterna para o tamanho real (100%) e de volta.
 * - Teclado: ← e → trocam de foto; Esc fecha (tratado pelo Modal).
 *
 * images: array de strings (data URL); startIndex: foto aberta; isOpen/onClose: controle do pai.
 */
export default function ImageViewerModal({ images, startIndex, isOpen, onClose }) {
    const { t } = useTranslation();
    const [ index, setIndex ] = useState(startIndex);
    const [ actualSize, setActualSize ] = useState(false);

    const total = images.length;
    const hasPrev = index > 0;
    const hasNext = index < total - 1;
    const src = images[index];

    const goTo = (i) => {
        setIndex(i);
        setActualSize(false);
    };

    // sempre que abre (ou muda a foto inicial), começa nela e no modo ajustado
    useEffect(() => {
        if(isOpen){
            setIndex(startIndex);
            setActualSize(false);
        }
    }, [isOpen, startIndex]);

    useEffect(() => {
        if(!isOpen) return;

        const handleKeyDown = (e) => {
            if(e.key === 'ArrowLeft' && hasPrev) goTo(index - 1);
            else if(e.key === 'ArrowRight' && hasNext) goTo(index + 1);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, index, hasPrev, hasNext]);

    return (
        <Modal size="xl" isOpen={isOpen} toggle={onClose} zIndex={VIEWER_Z_INDEX}>
            <ModalHeader
                toggle={onClose}
                close={<button className="btn-close" onClick={onClose}></button>}>
                    <i className="fa-regular fa-image" aria-hidden="true"></i> {t('imagens.modal.imagem')}
                    {total > 1 && <span className="ms-2 fs-14px">{index + 1} / {total}</span>}
            </ModalHeader>

            <ModalBody className="p-0">
                <div style={{ maxHeight: MAX_IMG_HEIGHT, overflow: 'auto' }}>
                    {src &&
                        <img
                            src={src}
                            className="d-block mx-auto"
                            style={actualSize
                                ? { cursor: 'zoom-out' }
                                : { maxWidth: '100%', maxHeight: MAX_IMG_HEIGHT, objectFit: 'contain', cursor: 'zoom-in' }}
                            onClick={() => setActualSize(!actualSize)}
                        />
                    }
                </div>
            </ModalBody>

            {total > 1 &&
                <ModalFooter className="justify-content-between">
                    <button type="button" className="btn btn-secondary" disabled={!hasPrev} onClick={() => goTo(index - 1)}>
                        {t('imagens.modal.anterior')}
                    </button>
                    <button type="button" className="btn btn-secondary" disabled={!hasNext} onClick={() => goTo(index + 1)}>
                        {t('imagens.modal.proxima')}
                    </button>
                </ModalFooter>
            }
        </Modal>
    );
}
